import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rn-vui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import rewardsTheme from '../../theme/rewardsTheme';

import TextInput from './TextInput';

const meta = {
  title: 'Components/TextInput',
  component: TextInput,
  argTypes: {
    label: {
      control: 'text',
    },
    placeholder: {
      control: 'text',
    },
    keyboardType: {
      control: 'select',
      options: ['default', 'email-address', 'numeric', 'phone-pad', 'number-pad'],
    },
    autoCapitalize: {
      control: 'select',
      options: ['none', 'sentences', 'words', 'characters'],
    },
    largeText: {
      control: 'boolean',
    },
    errorMessageWhenTouched: {
      control: 'text',
    },
  },
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <View style={styles.container}>
          <Story />
        </View>
      </ThemeProvider>
    ),
  ],
} satisfies Meta<typeof TextInput>;

export default meta;

type Story = StoryObj<typeof meta>;

export const AllVariants: Story = {
  render: () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [touched, setTouched] = useState(false);

    return (
      <View style={styles.variantsContainer}>
        <TextInput
          label="Phone Number:"
          placeholder="04XX XXX XXX"
          keyboardType="phone-pad"
          autoComplete="tel"
          value={phoneNumber}
          onChangeText={setPhoneNumber}
        />
        <TextInput
          label="Email Address:"
          placeholder="your@email.com"
          keyboardType="email-address"
          autoCapitalize="none"
          autoComplete="email"
          value={email}
          onChangeText={setEmail}
        />
        <TextInput
          label="Password:"
          placeholder="Enter password"
          secureTextEntry
          autoCapitalize="none"
          value={password}
          onChangeText={setPassword}
        />
        <TextInput
          label="With Error:"
          placeholder="Enter something"
          errorMessageWhenTouched="This field is required"
          touched={touched}
          onBlur={() => setTouched(true)}
        />
        <TextInput label="Large Text:" placeholder="Large text input" largeText />
      </View>
    );
  },
};

export const Default: Story = {
  render: () => {
    const [value, setValue] = useState('');
    return (
      <TextInput
        label="Your mobile number:"
        placeholder="04XX XXX XXX"
        keyboardType="phone-pad"
        autoComplete="tel"
        value={value}
        onChangeText={setValue}
      />
    );
  },
};

export const WithError: Story = {
  render: () => {
    const [value, setValue] = useState('');
    const [touched, setTouched] = useState(false);

    return (
      <TextInput
        label="Required Field:"
        placeholder="Enter value"
        value={value}
        onChangeText={setValue}
        onBlur={() => setTouched(true)}
        errorMessageWhenTouched={!value ? 'This field is required' : undefined}
        touched={touched}
      />
    );
  },
};

export const PhoneNumber: Story = {
  render: () => {
    const [value, setValue] = useState('');
    return (
      <TextInput
        label="Your mobile number:"
        placeholder="04XX XXX XXX"
        keyboardType="phone-pad"
        autoComplete="tel"
        autoCapitalize="none"
        maxLength={20}
        value={value}
        onChangeText={setValue}
      />
    );
  },
};

export const Email: Story = {
  render: () => {
    const [value, setValue] = useState('');
    return (
      <TextInput
        label="Email Address:"
        placeholder="your@email.com"
        keyboardType="email-address"
        autoCapitalize="none"
        autoComplete="email"
        value={value}
        onChangeText={setValue}
      />
    );
  },
};

export const Password: Story = {
  render: () => {
    const [value, setValue] = useState('');
    return (
      <TextInput
        label="Password:"
        placeholder="Enter your password"
        secureTextEntry
        autoCapitalize="none"
        autoComplete="password"
        value={value}
        onChangeText={setValue}
      />
    );
  },
};

export const LargeText: Story = {
  render: () => {
    const [value, setValue] = useState('');
    return (
      <TextInput
        label="Large Text Input:"
        placeholder="Large text"
        largeText
        value={value}
        onChangeText={setValue}
      />
    );
  },
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  variantsContainer: {
    gap: 16,
  },
});
